import { msg } from "../../locales/translate.ts";
import type { OverviewAnalysisResult, OverviewIssueDetail } from "../../modules/overviewAnalysis/types";

export type IssueLevel = "critical" | "warning" | "info";
export type IssueCategory = "bank" | "country" | "channel" | "email" | "technical" | "other";
export type IssueFilters = { category: IssueCategory | "all"; level: IssueLevel | "all" };

export function issueCategory(issue: { kind: string }): IssueCategory {
	return ["bank", "country", "channel", "email", "technical"].includes(issue.kind) ? issue.kind as IssueCategory : "other";
}

export function matchesIssueFilters(issue: { kind: string; level: IssueLevel }, filters: IssueFilters) {
	return (filters.category === "all" || issueCategory(issue) === filters.category)
		&& (filters.level === "all" || issue.level === filters.level);
}
export const issueLevels = {
	critical: { get label() { return msg("message_f186cf19"); }, color: "#b42332", background: "#fff1f2" },
	warning: { get label() { return msg("message_f17ceff0"); }, color: "#946000", background: "#fff7e6" },
	info: { get label() { return msg("message_574d43b4"); }, color: "#667085", background: "#f2f4f7" },
};
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
export const issueNumber = (value: number, locale = "ru-RU") => new Intl.NumberFormat(locale, {
	maximumFractionDigits: [70, 80].some(limit => value < limit && Math.round(value * 10) / 10 >= limit) ? 2 : 1,
}).format(value);

export function presentIssue(text: string, detail?: OverviewIssueDetail, observation = false, localizedTitles: Record<string, string> = {}) {
	// Never attach another finding's metrics or drill-down to stale text.
	const matched = detail?.text === text ? detail : undefined;
	const evidence = matched?.evidence;
	const attempts = finite(evidence?.attempts) ? evidence.attempts : finite(evidence?.current) ? evidence.current : undefined;
	const current = finite(evidence?.currentRate) ? evidence.currentRate : undefined;
	const previous = finite(evidence?.previousRate) ? evidence.previousRate : undefined;
	const count = finite(evidence?.count) ? evidence.count : matched?.technicalErrors?.reduce((sum, row) => sum + row.count, 0);
	const drop = current !== undefined && previous !== undefined ? Math.max(0, previous - current) : 0;
	const gap = current !== undefined ? Math.max(0, 80 - current) : 0;
	const volumeDrop = finite(evidence?.ratio) ? Math.max(0, (1 - evidence.ratio) * 100) : 0;
	const magnitude = Math.max(drop, gap, volumeDrop);
	const emailRule = matched?.entity?.type === "email" && evidence?.kind === "email_declines";
	const minimumSample = emailRule ? 5 : 30;
	const smallSample = attempts !== undefined && attempts < minimumSample;
	const supported = evidence?.targetSupported === true || evidence?.trendSupported === true;
	// Magnitude × exposure, discounted for uncertain/small samples. Capping
	// exposure prevents large segments with tiny gaps from dominating forever.
	const exposure = attempts === undefined ? 0.25 : Math.sqrt(Math.min(attempts, 10000) / 100) * attempts / (attempts + minimumSample);
	const score = observation ? -1 : magnitude > 0
		? magnitude * exposure * (supported || volumeDrop > 0 ? 1 : 0.5)
		: count !== undefined ? Math.sqrt(count) * 2 : matched?.severity === "critical" ? 20 : 1;
	const level: IssueLevel = observation || smallSample || matched?.severity === "info" ? "info"
		: emailRule ? matched?.severity === "critical" ? "critical" : "warning"
		: (((magnitude >= 20 || current !== undefined && current < 70) && supported || volumeDrop >= 40) && attempts !== undefined && attempts >= 30) || matched?.severity === "critical" ? "critical" : "warning";
	const kind = matched?.entity?.type || matched?.subject?.kind || (matched?.id === "technical_errors" ? "technical" : matched?.id.includes("volume") ? "volume" : matched?.id.endsWith("_growth") ? "refund" : "activity");
	const titles: Record<string, string> = { technical: msg("message_62f401e0"), volume: msg("message_ee0c7d14"), refund: msg("message_2b9981a0"), activity: msg("message_a9345566"), ...localizedTitles };
	const title = matched?.entity?.label || matched?.subject?.label || titles[kind] || titles.activity;
	const prefix = matched?.entity || matched?.subject ? text.indexOf(": ") : -1;
	const description = prefix >= 0 ? text.slice(prefix + 2) : text;
	return { text, detail: matched, attempts, current, previous, count, drop, gap, volumeDrop, score, level, kind, title, description, smallSample };
}

export function rankedOverviewIssues(result: OverviewAnalysisResult, localizedTitles: Record<string, string> = {}) {
	return result.issues.map((text, index) => ({ ...presentIssue(text, result.issueDetails?.[index], false, localizedTitles), index }))
		.sort((a, b) => b.score - a.score || a.index - b.index);
}
